'use client';

/**
 * JobFlowStory — the flagship scroll sequence. A sticky stage cycles through
 * the five product moments as the visitor scrolls: storm alert, instant
 * quote, pipeline, invoicing, AI voice. Each moment plays its own film while
 * active; a progress rail shows where you are in the flow. This is the
 * section that tells the product's story as a FLOW, not a stack of cards.
 *
 * Mechanics: the section is ~420vh tall; the stage is sticky for its whole
 * height; scrollYProgress maps to the active step. Only the active step and
 * its neighbors mount media (single decoder, poster-first), the film pauses
 * offscreen and via its own control (MP4 fallback when the WebM fails to
 * decode), and the whole thing sleeps when the section is offscreen.
 * Reduced motion: a plain stacked list of the steps. The stage chrome labels
 * the visuals as an illustrated demo — they are code-drawn, not captures.
 */
import { motion, useMotionValueEvent, useReducedMotion, useScroll } from 'framer-motion';
import { FLOW_EASE } from './motion';
import { Pause, Play } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatedSection } from './AnimatedSection';
import { PRODUCT_FRAMES, type ProductFrame } from '@/lib/productFrames';

const STEPS: { frame: ProductFrame; title: string; line: string }[] = [
  {
    frame: PRODUCT_FRAMES.canvassing,
    title: 'Storm hits. You knock first.',
    line: 'Territories draw themselves around the damage — routes, pins, and priorities ready before the trucks roll.',
  },
  {
    frame: PRODUCT_FRAMES.instant,
    title: 'The address prices itself.',
    line: 'A homeowner types an address and watches the roof measure and price while you watch the lead arrive.',
  },
  {
    frame: PRODUCT_FRAMES.pipeline,
    title: 'Every deal, one board.',
    line: 'From new lead to signed contract on a single board — nothing slips between tools.',
  },
  {
    frame: PRODUCT_FRAMES.invoices,
    title: 'Invoices pay themselves.',
    line: 'Branded invoices go out the moment the job closes, and the collected counter climbs.',
  },
  {
    frame: PRODUCT_FRAMES.ai,
    title: 'No call goes unanswered.',
    line: 'The AI receptionist answers at 2am, books the inspection, and puts it on the calendar.',
  },
];

export function JobFlowStory() {
  const reduced = useReducedMotion();
  if (reduced) {
    return (
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-sm font-medium uppercase tracking-[0.12em] text-white/40 mb-4">
              One flow, start to paid
            </p>
            <h2 className="display-headline text-3xl sm:text-4xl lg:text-5xl mb-4">
              The whole job, <span className="headline-gradient">in one motion.</span>
            </h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-x-14 gap-y-1 max-w-5xl mx-auto">
            {STEPS.map(({ frame, title, line }, i) => (
              <div
                key={frame.id}
                className="flex items-start gap-4 py-4 border-t border-white/[0.06]"
              >
                <span className="text-sm font-bold text-white/35 tabular-nums mt-0.5">
                  0{i + 1}
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-white">
                    {title} <span className="text-white/40">— {frame.label}</span>
                  </h3>
                  <p className="text-sm text-white/50 leading-relaxed mt-0.5">{line}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return <AnimatedJobFlowStory />;
}

function AnimatedJobFlowStory() {
  const sectionRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] });
  const [active, setActive] = useState(0);
  const [inView, setInView] = useState(false);
  const [filmPaused, setFilmPaused] = useState(false);
  const [useMp4, setUseMp4] = useState(false);

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    const step = Math.min(Math.floor(v * STEPS.length), STEPS.length - 1);
    setActive(step);
  });

  // Per-step media state resets; only the active step and its neighbors
  // mount media, so lazy posters actually defer and one decoder is live.
  useEffect(() => {
    setFilmPaused(false);
    setUseMp4(false);
  }, [active]);

  // Sleep when offscreen: the active film stops decoding.
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.25,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Keep the active film playing on step change, format swap, and wake —
  // unless the visitor paused it via the stage control.
  const visitorPausedRef = useRef(false);
  // Playback attempts are generation-counted: play-promise rejections from
  // superseded attempts (pause, step change, format swap) must not flip the
  // control. The label itself tracks the video element's own play/pause
  // events, so blocked autoplay still leaves Play available.
  const playAttemptRef = useRef(0);

  // Stable across renders: it reads only refs and a state setter.
  const requestPlay = useCallback(() => {
    const attempt = ++playAttemptRef.current;
    const el = videoRef.current;
    if (!el) return;
    void el
      .play()
      .then(() => {
        if (playAttemptRef.current === attempt) setFilmPaused(false);
      })
      .catch(() => {
        if (playAttemptRef.current === attempt) setFilmPaused(true);
      });
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !inView || visitorPausedRef.current) return;
    requestPlay();
  }, [active, useMp4, inView, requestPlay]);

  const toggleFilm = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      visitorPausedRef.current = false;
      requestPlay();
    } else {
      visitorPausedRef.current = true;
      playAttemptRef.current += 1; // supersede in-flight attempts
      el.pause();
    }
  };

  const activeStep = STEPS[active];
  const activeFrame = activeStep.frame;

  return (
    <section
      ref={sectionRef}
      className="relative"
      style={{ height: `${120 + STEPS.length * 76}vh` }}
    >
      <div
        ref={stageRef}
        className="sticky top-0 h-[100svh] flex flex-col justify-center overflow-y-auto lg:overflow-hidden pt-24 pb-4 lg:py-24"
      >
        {/* quiet single light source */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[420px]"
          style={{
            background:
              'radial-gradient(60% 100% at 50% 0%, rgb(255 255 255 / 0.04) 0%, transparent 70%)',
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full h-full lg:h-auto grid grid-rows-[auto_minmax(72px,1fr)] lg:grid-rows-1 lg:grid-cols-[1fr_1.6fr] gap-4 lg:gap-16 items-center max-h-full">
          {/* copy + rail */}
          <div className="min-w-0">
            <p className="text-sm font-medium uppercase tracking-[0.12em] text-white/40 mb-2 lg:mb-4">
              One flow, start to paid
            </p>
            <div className="relative grid lg:min-h-[200px]">
              {STEPS.map(({ title, line }, i) => (
                <motion.div
                  key={title}
                  className="col-start-1 row-start-1"
                  initial={false}
                  animate={
                    i === active ? { opacity: 1, y: 0 } : { opacity: 0, y: i < active ? -14 : 14 }
                  }
                  transition={{ duration: 0.45, ease: FLOW_EASE }}
                  aria-hidden={i !== active}
                >
                  <h2 className="display-headline text-3xl sm:text-4xl mb-2 lg:mb-4">{title}</h2>
                  <p className="text-lg text-white/55 leading-relaxed max-w-md">{line}</p>
                </motion.div>
              ))}
            </div>
            {/* progress rail */}
            <div className="flex items-center gap-3 mt-4 lg:mt-8" aria-hidden>
              {STEPS.map(({ frame, title }, i) => (
                <div
                  key={frame.id}
                  className="flex-1 h-[3px] rounded-full bg-white/[0.08] overflow-hidden"
                >
                  <motion.div
                    className="h-full bg-white origin-left"
                    initial={false}
                    animate={{ scaleX: i <= active ? 1 : 0 }}
                    style={{ opacity: i <= active ? 1 : 0.35 }}
                    transition={{ duration: 0.4, ease: FLOW_EASE }}
                  />
                  <span className="sr-only">{title}</span>
                </div>
              ))}
            </div>
            <p className="mt-2 lg:mt-4 text-sm text-white/35" aria-live="polite">
              Step {active + 1} of {STEPS.length}: {activeStep.title}
            </p>
          </div>

          {/* stage: the active film, one decoder at a time */}
          <div className="relative mx-auto w-full max-w-full h-full lg:h-[clamp(240px,46svh,560px)] overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0c] shadow-[0_32px_80px_-24px_rgba(0,0,0,0.65)]">
            {STEPS.map(({ frame }, i) => {
              // Adjacent window only: distant steps never mount media, so
              // nothing loads until the flow reaches it (lazy hints cannot
              // defer media inside a sticky, always-on-screen stage).
              const near = Math.abs(i - active) <= 1;
              const isActive = i === active;
              if (!near) return null;
              return (
                <motion.div
                  key={frame.id}
                  className="absolute inset-0"
                  initial={false}
                  animate={{ opacity: isActive ? 1 : 0, scale: isActive ? 1 : 1.03 }}
                  transition={{ duration: 0.55, ease: FLOW_EASE }}
                  aria-hidden={!isActive}
                >
                  <img
                    src={frame.still}
                    alt={isActive ? frame.alt : ''}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="eager"
                  />
                  {isActive && inView && frame.video ? (
                    <video
                      ref={videoRef}
                      onPlay={() => setFilmPaused(false)}
                      onPause={() => setFilmPaused(true)}
                      className="absolute inset-0 w-full h-full object-cover"
                      src={useMp4 ? frame.video.replace(/\.webm$/, '.mp4') : frame.video}
                      onError={() => setUseMp4(true)}
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  ) : null}
                </motion.div>
              );
            })}
            {/* stage chrome: illustrated-demo disclosure + playback control */}
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10 gap-2">
              <span className="text-[11px] text-white/60 tracking-wide bg-black/50 backdrop-blur px-3 py-1.5 rounded-lg border border-white/10 truncate">
                Rooflyzer OS · Summit Ridge Roofing · {activeFrame.label}
                <span className="text-white/35"> · illustrated demo</span>
              </span>
              {activeFrame.video ? (
                <button
                  type="button"
                  onClick={toggleFilm}
                  aria-label={filmPaused ? 'Play product video' : 'Pause product video'}
                  aria-pressed={filmPaused}
                  className="shrink-0 inline-flex min-h-9 min-w-9 items-center justify-center rounded-lg bg-black/50 backdrop-blur border border-white/10 text-white/80 hover:text-white"
                >
                  {filmPaused ? <Play className="size-3.5" /> : <Pause className="size-3.5" />}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
